import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, ArrowRight, Plus, Trash2, Camera, CheckCircle2, Loader2, X, ImagePlus, FileText, Video } from "lucide-react";
import { detectBrand, BRAND_META } from "@/components/Logo";
import Logo from "@/components/Logo";

const STEPS = [
    { id: 1, title: "Dine oplysninger", short: "Dig" },
    { id: 2, title: "Produktet", short: "Produkt" },
    { id: 3, title: "Problemet", short: "Problem" },
    { id: 4, title: "Billeder og kvittering", short: "Billeder" },
    { id: 5, title: "Gennemse og send", short: "Send" },
];

const EMPTY_PRODUCT = { room: "", product_type: "", color_model: "", quantity: 1 };

export default function PublicForm() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [meta, setMeta] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const brand = detectBrand();
    const brandMeta = BRAND_META[brand] || BRAND_META.gardinbussen;

    const [form, setForm] = useState({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        customer_address: "",
        customer_postal: "",
        customer_city: "",
        order_number: "",
        products: [{ ...EMPTY_PRODUCT }],
        problem_category: "",
        problem_description: "",
        wished_solution: "",
        consent: false,
    });

    const [images, setImages] = useState([]); // {id, filename, size, content_type, localUrl}
    const [receipt, setReceipt] = useState(null);
    const [drawing, setDrawing] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        api.get("/meta/public").then((r) => setMeta(r.data)).catch(() => {});
    }, []);

    const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const updateProduct = (idx, key, value) => {
        setForm((prev) => {
            const products = [...prev.products];
            products[idx] = { ...products[idx], [key]: value };
            return { ...prev, products };
        });
    };

    const addProduct = () => setForm((p) => ({ ...p, products: [...p.products, { ...EMPTY_PRODUCT }] }));
    const removeProduct = (idx) =>
        setForm((p) => ({
            ...p,
            products: p.products.length > 1 ? p.products.filter((_, i) => i !== idx) : p.products,
        }));

    const validateStep = (s) => {
        if (s === 1) {
            const required = ["customer_name", "customer_email", "customer_phone", "customer_address", "customer_postal", "customer_city"];
            for (const k of required) if (!form[k].trim()) return `Udfyld venligst alle påkrævede felter`;
            if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.customer_email)) return "Ugyldig email";
        }
        if (s === 2) {
            if (!form.products.length) return "Tilføj mindst ét produkt";
            for (const p of form.products) {
                if (!p.product_type.trim()) return "Vælg produkttype for alle produkter";
            }
        }
        if (s === 3) {
            if (!form.problem_category) return "Vælg problemkategori";
            if (!form.problem_description.trim() || form.problem_description.trim().length < 10)
                return "Beskriv problemet (mindst 10 tegn)";
        }
        if (s === 4) {
            const videoExts = ["mp4", "mov", "m4v", "webm", "3gp", "avi", "mkv", "qt"];
            const isVideoFile = (f) => videoExts.includes((f.ext || "").toLowerCase()) || (f.content_type || "").startsWith("video/");
            const nonVideoCount = images.filter((f) => !isVideoFile(f)).length;
            const videoCount = images.filter(isVideoFile).length;
            if (nonVideoCount < 3) return "Der skal uploades mindst 3 billeder";
            if (videoCount < 1) return "Der skal uploades mindst 1 kort video (5–10 sekunder)";
            if (!receipt) return "Upload venligst kvittering eller faktura";
        }
        if (s === 5) {
            if (!form.consent) return "Accepter samtykket for at fortsætte";
        }
        return null;
    };

    const next = () => {
        const err = validateStep(step);
        if (err) {
            toast.error(err);
            return;
        }
        setStep((s) => Math.min(5, s + 1));
    };
    const prev = () => setStep((s) => Math.max(1, s - 1));

    const handleFileUpload = async (fileList, slot = "image") => {
        // slot: "image" | "receipt" | "drawing"
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setUploading(true);
        const maxMB = meta?.max_file_size_mb || 15;
        const maxVideoMB = meta?.max_video_size_mb || 20;
        const videoExts = ["mp4", "mov", "m4v", "webm", "3gp", "avi", "mkv", "qt"];
        const imgExts = ["jpg", "jpeg", "png", "heic", "heif", "webp", "pdf"];
        let successCount = 0;
        try {
            for (const file of files) {
                const ext = file.name.split(".").pop()?.toLowerCase() || "";
                const isVideo = videoExts.includes(ext) || (file.type || "").startsWith("video/");
                if (isVideo) {
                    if (file.size > maxVideoMB * 1024 * 1024) {
                        toast.error(`Videoen er for stor (max ${maxVideoMB} MB). Optag venligst en kortere video på 5–10 sekunder.`);
                        continue;
                    }
                } else {
                    if (file.size > maxMB * 1024 * 1024) {
                        toast.error(`${file.name} er for stor (max ${maxMB} MB)`);
                        continue;
                    }
                    if (!imgExts.includes(ext)) {
                        toast.error(`${file.name}: filtype ikke tilladt`);
                        continue;
                    }
                }
                try {
                    const fd = new FormData();
                    fd.append("file", file);
                    const { data } = await api.post("/public/upload", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    const record = {
                        ...data,
                        localUrl: URL.createObjectURL(file),
                    };
                    if (slot === "receipt") {
                        setReceipt(record);
                    } else if (slot === "drawing") {
                        setDrawing(record);
                    } else {
                        setImages((prev) => [...prev, record]);
                    }
                    successCount++;
                } catch (e) {
                    toast.error(`${file.name}: ${e.response?.data?.detail || "upload fejlede"}`);
                }
            }
            if (successCount > 0) {
                toast.success(`${successCount} fil${successCount > 1 ? "er" : ""} uploadet`);
            }
        } finally {
            setUploading(false);
        }
    };

    const removeImage = (id) => setImages((prev) => prev.filter((i) => i.id !== id));

    const submit = async () => {
        const err = validateStep(5) || validateStep(4) || validateStep(3) || validateStep(2) || validateStep(1);
        if (err) {
            toast.error(err);
            return;
        }
        setSubmitting(true);
        try {
            const extraFileIds = drawing?.id ? [drawing.id] : [];
            const payload = {
                ...form,
                brand,
                file_ids: [...images.map((i) => i.id), ...extraFileIds],
                receipt_file_id: receipt?.id || null,
            };
            const { data } = await api.post("/public/complaints", payload);
            navigate(`/kvittering/${data.case_number}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke sende reklamation");
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAF9]">
            <header className="border-b border-[#E5E1DA] bg-white">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Logo size="sm" subtitle={null} />
                    <div className="text-xs text-[#78736E] uppercase tracking-wider">Reklamation</div>
                </div>
            </header>

            {/* Stepper */}
            <div className="max-w-3xl mx-auto px-6 pt-8">
                <div className="flex items-center justify-between gap-2">
                    {STEPS.map((s, idx) => {
                        const active = step === s.id;
                        const done = step > s.id;
                        return (
                            <div key={s.id} className="flex items-center flex-1 last:flex-initial">
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                                            active
                                                ? "bg-[color:var(--brand-accent)] text-white"
                                                : done
                                                ? "bg-[#D2C9B9] text-[#2C2A29]"
                                                : "bg-[#F0EBE1] text-[#78736E]"
                                        }`}
                                        data-testid={`step-indicator-${s.id}`}
                                    >
                                        {done ? <CheckCircle2 className="w-4 h-4" /> : s.id}
                                    </div>
                                    <div className={`text-[10px] mt-1.5 font-medium uppercase tracking-wider hidden sm:block ${active ? "text-[#2C2A29]" : "text-[#78736E]"}`}>
                                        {s.short}
                                    </div>
                                </div>
                                {idx < STEPS.length - 1 && (
                                    <div className={`flex-1 h-px mx-2 ${done ? "bg-[#D2C9B9]" : "bg-[#E5E1DA]"}`} />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <main className="max-w-3xl mx-auto px-6 py-10">
                <div className="bg-white border border-[#E5E1DA] rounded-2xl p-6 sm:p-10 shadow-[0_8px_32px_rgba(44,42,41,0.04)]">
                    <div className="mb-8">
                        <div className="text-xs font-semibold uppercase tracking-[0.1em] text-[#78736E]">
                            Trin {step} af {STEPS.length}
                        </div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#2C2A29] mt-1">
                            {STEPS[step - 1].title}
                        </h2>
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={step}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.25 }}
                        >
                            {step === 1 && <Step1Customer form={form} update={update} />}
                            {step === 2 && (
                                <Step2Product
                                    form={form}
                                    updateProduct={updateProduct}
                                    addProduct={addProduct}
                                    removeProduct={removeProduct}
                                    productTypes={meta?.product_types || []}
                                />
                            )}
                            {step === 3 && (
                                <Step3Problem
                                    form={form}
                                    update={update}
                                    categories={meta?.problem_categories || []}
                                />
                            )}
                            {step === 4 && (
                                <Step4Files
                                    images={images}
                                    receipt={receipt}
                                    setReceipt={setReceipt}
                                    drawing={drawing}
                                    setDrawing={setDrawing}
                                    removeImage={removeImage}
                                    onUpload={handleFileUpload}
                                    uploading={uploading}
                                    maxMB={meta?.max_file_size_mb || 15}
                                    maxVideoMB={meta?.max_video_size_mb || 100}
                                    brand={brand}
                                />
                            )}
                            {step === 5 && (
                                <Step5Review form={form} images={images} receipt={receipt} meta={meta} update={update} brandMeta={brandMeta} />
                            )}
                        </motion.div>
                    </AnimatePresence>

                    <Separator className="my-8 bg-[#E5E1DA]" />

                    <div className="flex items-center justify-between gap-3">
                        <Button
                            variant="ghost"
                            onClick={prev}
                            disabled={step === 1 || submitting}
                            data-testid="form-prev-button"
                            className="text-[#5C5854] hover:bg-[#F0EBE1]"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Tilbage
                        </Button>
                        {step < 5 ? (
                            <Button
                                onClick={next}
                                data-testid="form-next-button"
                                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white"
                            >
                                Næste
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        ) : (
                            <Button
                                onClick={submit}
                                disabled={submitting || !form.consent}
                                data-testid="form-submit-button"
                                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                Send reklamation
                            </Button>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}

function Field({ label, required, children, hint }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-sm font-medium text-[#2C2A29]">
                {label} {required && <span className="text-[#993C3C]">*</span>}
            </Label>
            {children}
            {hint && <div className="text-xs text-[#78736E]">{hint}</div>}
        </div>
    );
}

function Step1Customer({ form, update }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Fulde navn" required>
                <Input data-testid="form-customer-name" value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} placeholder="Anne Jensen" />
            </Field>
            <Field label="Email" required>
                <Input type="email" data-testid="form-customer-email" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} placeholder="din@email.dk" />
            </Field>
            <Field label="Telefon" required>
                <Input data-testid="form-customer-phone" value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} placeholder="+45 12 34 56 78" />
            </Field>
            <Field label="Ordrenummer / fakturanummer">
                <Input data-testid="form-order-number" value={form.order_number} onChange={(e) => update("order_number", e.target.value)} placeholder="valgfrit" />
            </Field>
            <Field label="Adresse" required>
                <Input data-testid="form-customer-address" value={form.customer_address} onChange={(e) => update("customer_address", e.target.value)} placeholder="Gadenavn 12" />
            </Field>
            <div className="grid grid-cols-2 gap-5">
                <Field label="Postnr." required>
                    <Input data-testid="form-customer-postal" value={form.customer_postal} onChange={(e) => update("customer_postal", e.target.value)} placeholder="2100" />
                </Field>
                <Field label="By" required>
                    <Input data-testid="form-customer-city" value={form.customer_city} onChange={(e) => update("customer_city", e.target.value)} placeholder="København Ø" />
                </Field>
            </div>
        </div>
    );
}

function Step2Product({ form, updateProduct, addProduct, removeProduct, productTypes }) {
    return (
        <div className="space-y-5">
            <p className="text-sm text-[#5C5854] leading-relaxed">
                Vælg hvilke produkter reklamationen handler om. Tilføj flere linjer hvis det gælder flere gardiner.
            </p>
            {form.products.map((p, idx) => (
                <div key={idx} className="border border-[#E5E1DA] rounded-xl p-5 bg-[#FAFAF9]">
                    <div className="flex items-center justify-between mb-4">
                        <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Produkt {idx + 1}</div>
                        {form.products.length > 1 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeProduct(idx)}
                                data-testid={`remove-product-${idx}`}
                                className="text-[#993C3C] hover:bg-red-50"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Rum">
                            <Input
                                value={p.room}
                                onChange={(e) => updateProduct(idx, "room", e.target.value)}
                                placeholder="f.eks. Stue"
                                data-testid={`product-room-${idx}`}
                            />
                        </Field>
                        <Field label="Produkttype" required>
                            <Select value={p.product_type} onValueChange={(v) => updateProduct(idx, "product_type", v)}>
                                <SelectTrigger data-testid={`product-type-${idx}`}>
                                    <SelectValue placeholder="Vælg type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {productTypes.map((t) => (
                                        <SelectItem key={t} value={t}>
                                            {t}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Farve / model">
                            <Input
                                value={p.color_model}
                                onChange={(e) => updateProduct(idx, "color_model", e.target.value)}
                                placeholder="hvis kendt"
                                data-testid={`product-color-${idx}`}
                            />
                        </Field>
                        <Field label="Antal">
                            <Input
                                type="number"
                                min={1}
                                value={p.quantity}
                                onChange={(e) => updateProduct(idx, "quantity", Math.max(1, parseInt(e.target.value || "1")))}
                                data-testid={`product-qty-${idx}`}
                            />
                        </Field>
                    </div>
                </div>
            ))}
            <Button
                variant="outline"
                onClick={addProduct}
                data-testid="add-product-button"
                className="w-full border-dashed border-[#D2C9B9] bg-transparent hover:bg-[#F0EBE1]"
            >
                <Plus className="w-4 h-4 mr-2" />
                Tilføj endnu et produkt
            </Button>
        </div>
    );
}

function Step3Problem({ form, update, categories }) {
    return (
        <div className="space-y-5">
            <Field label="Problemkategori" required>
                <Select value={form.problem_category} onValueChange={(v) => update("problem_category", v)}>
                    <SelectTrigger data-testid="form-problem-category">
                        <SelectValue placeholder="Vælg kategori" />
                    </SelectTrigger>
                    <SelectContent>
                        {categories.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                                {c.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </Field>
            <Field label="Beskriv problemet så præcist som muligt" required hint="Minimum 10 tegn">
                <Textarea
                    rows={6}
                    data-testid="form-problem-description"
                    value={form.problem_description}
                    onChange={(e) => update("problem_description", e.target.value)}
                    placeholder="Beskriv hvad der er galt, hvornår det skete, og alt der kan hjælpe os med at forstå situationen..."
                />
            </Field>
        </div>
    );
}

function Step4Files({ images, receipt, setReceipt, drawing, setDrawing, removeImage, onUpload, uploading, maxMB, maxVideoMB, brand }) {
    const cameraInputRef = useRef(null);
    const galleryInputRef = useRef(null);
    const receiptInputRef = useRef(null);
    const drawingInputRef = useRef(null);

    return (
        <div className="space-y-8">
            <div>
                <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium text-[#2C2A29]">
                        Billeder af problemet <span className="text-[#993C3C]">*</span>
                    </Label>
                    <div className="text-xs text-[#78736E]" data-testid="image-count-indicator">
                        {images.length} / min. 3
                    </div>
                </div>
                <div className="text-xs text-[#78736E] mb-2">JPG, PNG, HEIC eller PDF · max {maxMB} MB pr. fil</div>
                <div className="text-xs text-[#2C2A29] mb-4 p-2 rounded bg-[#F0EBE1] border border-[#D2C9B9]">
                    <strong>Video kræves:</strong> Upload en kort video på <strong>5–10 sekunder</strong> der viser problemet. Max {maxVideoMB} MB — er filen større skal du optage en kortere video.
                </div>

                {/* Hidden inputs */}
                <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    data-testid="camera-input"
                    onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length) onUpload(files);
                        e.target.value = "";
                    }}
                />
                <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*,video/*,.heic,.heif,application/pdf"
                    multiple
                    className="hidden"
                    data-testid="gallery-input"
                    onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length) onUpload(files);
                        e.target.value = "";
                    }}
                />

                {/* Action buttons */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => cameraInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="camera-button"
                        className="border-[#D2C9B9] bg-white hover:bg-[#F0EBE1] h-auto py-3 flex-col gap-1"
                    >
                        {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                        <span className="text-xs">Tag billede</span>
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => galleryInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="gallery-button"
                        className="border-[#D2C9B9] bg-white hover:bg-[#F0EBE1] h-auto py-3 flex-col gap-1"
                    >
                        {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                        <span className="text-xs">Vælg fra enhed</span>
                    </Button>
                </div>

                {/* Image previews */}
                {images.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {images.map((img) => (
                            <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden border border-[#E5E1DA] bg-[#F4F2EF]">
                                {img.ext === "pdf" ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                                        <FileText className="w-6 h-6 text-[#78736E] mb-1" />
                                        <div className="text-[10px] text-[#78736E] truncate w-full">{img.filename}</div>
                                    </div>
                                ) : (img.content_type || "").startsWith("video/") || ["mp4","mov","m4v","webm","3gp","avi","mkv","qt"].includes(img.ext) ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center bg-[#2C2A29]">
                                        <Video className="w-7 h-7 text-white mb-1" />
                                        <div className="text-[10px] text-white/80 truncate w-full">{img.filename}</div>
                                    </div>
                                ) : (
                                    <img src={img.localUrl} alt={img.filename} className="w-full h-full object-cover" />
                                )}
                                <button
                                    type="button"
                                    onClick={() => removeImage(img.id)}
                                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                                    data-testid={`remove-image-${img.id}`}
                                    aria-label="Fjern billede"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {images.length === 0 && (
                    <div className="border-2 border-dashed border-[#D2C9B9] rounded-lg p-8 text-center bg-[#FAFAF9]">
                        <Camera className="w-8 h-8 text-[#78736E] mx-auto mb-2" />
                        <div className="text-sm text-[#5C5854]">Ingen billeder tilføjet endnu</div>
                        <div className="text-xs text-[#78736E] mt-1">Brug knapperne ovenfor til at uploade</div>
                    </div>
                )}
            </div>

            <Separator className="bg-[#E5E1DA]" />

            <div>
                <Label className="text-sm font-medium text-[#2C2A29] block mb-2">Kvittering / faktura *</Label>
                <div className="text-xs text-[#78736E] mb-3">Påkrævet – for at vi kan håndtere din reklamation korrekt</div>
                <input
                    ref={receiptInputRef}
                    type="file"
                    accept="image/*,.heic,.heif,application/pdf"
                    className="hidden"
                    data-testid="receipt-input"
                    onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length) onUpload(files, "receipt");
                        e.target.value = "";
                    }}
                />
                {receipt ? (
                    <div className="flex items-center gap-3 p-3 border border-[#E5E1DA] rounded-lg bg-[#FAFAF9]">
                        <FileText className="w-5 h-5 text-[#4A4641]" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-[#2C2A29] truncate">{receipt.filename}</div>
                            <div className="text-xs text-[#78736E]">{(receipt.size / 1024).toFixed(1)} KB</div>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setReceipt(null)}
                            data-testid="remove-receipt"
                            aria-label="Fjern kvittering"
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                ) : (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => receiptInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="receipt-upload-button"
                        className="w-full border-dashed border-[#D2C9B9] bg-transparent hover:bg-[#F0EBE1] h-auto py-4"
                    >
                        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                        Upload kvittering eller faktura
                    </Button>
                )}
            </div>

            {brand === "gardinfabrikken" && (
                <>
                    <Separator className="bg-[#E5E1DA]" />
                    <div>
                        <Label className="text-sm font-medium text-[#2C2A29] block mb-2">Plantegning <span className="text-[#78736E] font-normal">(valgfri)</span></Label>
                        <div className="text-xs text-[#78736E] mb-3">
                            Har du en plantegning? Så markér hvilket gardin reklamationen drejer sig om, og vedhæft den her.
                        </div>
                        <input
                            ref={drawingInputRef}
                            type="file"
                            accept="image/*,.heic,.heif,application/pdf"
                            className="hidden"
                            data-testid="drawing-input"
                            onChange={(e) => {
                                const files = e.target.files;
                                if (files && files.length) onUpload(files, "drawing");
                                e.target.value = "";
                            }}
                        />
                        {drawing ? (
                            <div className="flex items-center gap-3 p-3 border border-[#E5E1DA] rounded-lg bg-[#FAFAF9]" data-testid="drawing-preview">
                                <FileText className="w-5 h-5 text-[#4A4641]" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-[#2C2A29] truncate">{drawing.filename}</div>
                                    <div className="text-xs text-[#78736E]">{(drawing.size / 1024).toFixed(1)} KB</div>
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDrawing(null)}
                                    data-testid="remove-drawing"
                                    aria-label="Fjern plantegning"
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        ) : (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => drawingInputRef.current?.click()}
                                disabled={uploading}
                                data-testid="drawing-upload-button"
                                className="w-full border-dashed border-[#D2C9B9] bg-transparent hover:bg-[#F0EBE1] h-auto py-4"
                            >
                                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                                Upload plantegning
                            </Button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function Step5Review({ form, images, receipt, meta, update, brandMeta }) {
    const catLabel = meta?.problem_categories?.find((c) => c.id === form.problem_category)?.label || form.problem_category;
    return (
        <div className="space-y-6">
            <Section title="Dine oplysninger">
                <KV k="Navn" v={form.customer_name} />
                <KV k="Email" v={form.customer_email} />
                <KV k="Telefon" v={form.customer_phone} />
                <KV k="Adresse" v={`${form.customer_address}, ${form.customer_postal} ${form.customer_city}`} />
                {form.order_number && <KV k="Ordrenummer" v={form.order_number} />}
            </Section>

            <Section title="Produkt(er)">
                {form.products.map((p, i) => (
                    <div key={i} className="text-sm">
                        <span className="font-medium">{p.product_type}</span>
                        {p.room && <span className="text-[#78736E]"> · {p.room}</span>}
                        {p.color_model && <span className="text-[#78736E]"> · {p.color_model}</span>}
                        <span className="text-[#78736E]"> · {p.quantity} stk.</span>
                    </div>
                ))}
            </Section>

            <Section title="Problem">
                <KV k="Kategori" v={catLabel} />
                <KV k="Beskrivelse" v={form.problem_description} />
            </Section>

            <Section title="Billeder og kvittering">
                <div className="text-sm">
                    {images.length} billede(r) · {receipt ? "Kvittering vedhæftet" : "Ingen kvittering"}
                </div>
            </Section>

            <div className="p-4 bg-[#F0EBE1] rounded-lg">
                <div className="flex items-start gap-3">
                    <Checkbox
                        id="consent"
                        checked={form.consent}
                        onCheckedChange={(v) => update("consent", !!v)}
                        data-testid="form-consent-checkbox"
                        className="mt-0.5"
                    />
                    <label htmlFor="consent" className="text-sm text-[#2C2A29] leading-relaxed cursor-pointer">
                        Jeg accepterer, at de indtastede oplysninger bruges af {brandMeta.name} til behandling af denne reklamation, og at relevante oplysninger og billeder må videresendes til leverandør eller underleverandør.
                    </label>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="border border-[#E5E1DA] rounded-xl p-5 bg-[#FAFAF9]">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-3">{title}</div>
            <div className="space-y-1.5">{children}</div>
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div className="text-sm">
            <span className="text-[#78736E] mr-2">{k}:</span>
            <span className="text-[#2C2A29] whitespace-pre-wrap">{v || "-"}</span>
        </div>
    );
}
